import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  FontSizeType,
  GoodData,
  TaskEnum,
  TaskGoodsUnusualDistrData,
} from '../../../types';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Grid,
  Row,
  Select,
  Space,
  Spin,
  Table,
  TablePaginationConfig,
  Tooltip,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import FourZeroThree from '../../FourZeroThree';
import FiveHundred from '../../FiveHundred';
import { CloseCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import {
  DATE_FORMAT,
  DEFAULT_FONT_SIZE,
  DEFAULT_SIZE_TYPE,
  GENERAL_TIMEOUT,
} from '../../../constants/systemConstants';
import { getDataWithAuthToken } from '../../../utils/axiosRequest';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { alertMessage } from '../../../utils/alertMessage';
import TableToolbar from '../../table/TableToolbar';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import { useVT } from 'virtualizedtableforantd4';
import { hasPermission } from '../../../utils/hasPermission';
import { actionPermissions } from '../../../constants/actionPermissions';
import SelectGoodsModal from '../../goods/selectGoods/SelectGoodsModal';
import {
  ColumnsType,
  FilterValue,
  SorterResult,
} from 'antd/lib/table/interface';
import { setFont } from '../../../utils/colComponents';
import { dashboardRoute } from '../../../constants/pathname';
import TableFooterToolbar from '../../table/TableFooterToolbar';
import { useTab } from '../../../hooks/useTab';

type TaskGoodsUnusualDetailProps = {
  taskEnum?: TaskEnum;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
};

const TaskGoodsUnusualDetail = ({
  taskEnum,
  isLoading,
  setIsLoading,
}: TaskGoodsUnusualDetailProps) => {
  //General Components
  const [fourZeroThree, setFourZeroThree] = useState<boolean>(false);
  const [fiveHundred, setFiveHundred] = useState(false);
  const { RangePicker } = DatePicker;
  const { addTab } = useTab();
  const isSubscribed = useRef(true);
  const formRef = useRef(null);
  const [form] = Form.useForm();
  const { t } = useTranslation();
  //Text Components
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout>();
  //Data Components
  const [selectGoodsVisible, setSelectGoodsVisible] = useState<boolean>(false);
  const [selectedGoodObjs, setSelectedGoodObjs] = useState<GoodData>();
  const [good, setGood] = useState<GoodData>();
  const [unusualData, setUnusualData] = useState<TaskGoodsUnusualDistrData[]>();
  const [sortValue, setSortValue] = useState<'UNUSUAL_NUM' | 'UNUSUAL_RATE'>(
    'UNUSUAL_NUM'
  );
  // Table Components
  const [fontSize, setFontSize] = useState<FontSizeType>(DEFAULT_FONT_SIZE);
  const [tableSize, setTableSize] = useState<SizeType>(DEFAULT_SIZE_TYPE);
  const screens = Grid.useBreakpoint();
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useLocalStorage('pageSize', '10');
  const [totalItem, setTotalItem] = useState(0);
  const [vt] = useVT(() => ({ scroll: { y: 600 } }), []);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<TaskGoodsUnusualDistrData[]>(
    []
  );

  const columnKeys = [
    'goodsId',
    'fullGoodsName',
    'totalNum',
    'unusualNum',
    'unusualRate',
  ];
  const [selectedColumns, setSelectedColumns] = useState(
    columnKeys.filter((key) => key !== 'goodsId')
  );

  const columns: ColumnsType<TaskGoodsUnusualDistrData> = [
    {
      title: setFont(t('analysis.taskGoodsUnusualDetail.goodsId'), fontSize),
      key: 'goodsId',
      dataIndex: 'goodsId',
      fixed: screens.lg ? 'left' : undefined,
      width: 20,
      render: (value) => (
        <Button
          style={{ padding: 0, fontSize: fontSize }}
          type="link"
          onClick={() => {
            addTab('', `${dashboardRoute.goods.detail}?good_id=${value}`);
          }}
          disabled={!hasPermission(actionPermissions.goodGroup.goodView)}
        >
          {value}
        </Button>
      ),
    },
    {
      title: setFont(t('analysis.taskGoodsUnusualDetail.goodsName'), fontSize),
      key: 'fullGoodsName',
      dataIndex: 'fullGoodsName',
      width: 200,
      render: (value, record) =>
        record &&
        hasPermission(actionPermissions.goodGroup.goodView) && (
          <Tooltip title={value}>
            <Button
              type="link"
              style={{
                padding: 0,
                textAlign: 'left',
                whiteSpace: 'normal',
                height: 'auto',
              }}
              disabled={!hasPermission(actionPermissions.goodGroup.goodView)}
              onClick={() => {
                addTab(
                  '',
                  `${dashboardRoute.goods.detail}?good_id=${record.goodsId}`
                );
              }}
            >
              {value}
            </Button>
          </Tooltip>
        ),
    },

    {
      title: setFont(t('analysis.taskGoodsUnusualDetail.totalNum'), fontSize),
      key: 'totalNum',
      dataIndex: 'totalNum',
      width: 100,
      render: (value, record) => (
        <Button
          style={{ padding: 0, fontSize: fontSize }}
          type="link"
          onClick={() => {
            addTab(
              '',
              `${dashboardRoute.sortationSystem.taskOrder}?order_ids=${record.orderIds}`
            );
          }}
          disabled={!hasPermission(actionPermissions.sortationGroup.taskView)}
        >
          {value}
        </Button>
      ),
    },
    {
      title: setFont(t('analysis.taskGoodsUnusualDetail.unusualNum'), fontSize),
      key: 'unusualNum',
      dataIndex: 'unusualNum',
      width: 100,
      sorter: true,
      render: (value, record) => (
        <Button
          style={{ padding: 0, fontSize: fontSize }}
          type="link"
          onClick={() => {
            addTab(
              '',
              `${dashboardRoute.sortationSystem.taskOrder}?order_ids=${record.unusualOrderIds}`
            );
          }}
          disabled={!hasPermission(actionPermissions.sortationGroup.taskView)}
        >
          {value}
        </Button>
      ),
    },

    {
      title: setFont(
        t('analysis.taskGoodsUnusualDetail.unusualRate'),
        fontSize
      ),
      key: 'unusualRate',
      dataIndex: 'unusualRate',
      width: 100,
      sorter: true,
      render: (value) => setFont(value, fontSize),
    },
  ];

  const getFormParams = useCallback(() => {
    return {
      page: page,
      size: pageSize,
      sortValue: sortValue,
      startDate:
        formRef.current && form.getFieldValue('date')
          ? moment(form.getFieldValue('date')[0]).format(DATE_FORMAT)
          : undefined,
      endDate:
        formRef.current && form.getFieldValue('date')
          ? moment(form.getFieldValue('date')[1]).format(DATE_FORMAT)
          : undefined,

      goodsId:
        formRef.current && form.getFieldValue('goodsId')
          ? form.getFieldValue('goodsId')
          : undefined,

      taskGoodsStatus:
        formRef.current && form.getFieldValue('taskGoodsStatus')
          ? form.getFieldValue('taskGoodsStatus').join()
          : undefined,
    };
  }, [page, pageSize, sortValue, form]);

  const getData = useCallback(async () => {
    try {
      if (isSubscribed.current) setIsLoading(true);
      const response = await getDataWithAuthToken(
        'analysis/task/task_goods/unusual',
        {
          params: getFormParams(),
        }
      );

      if (response && response.goodStatus) {
        setUnusualData(response.data.list);
        setTotalItem(response.data.totalItem);
      } else if (response && response.returnCode === 403) {
        setFourZeroThree(true);
      } else {
        setFiveHundred(true);
        alertMessage(
          'error',
          response?.msg || t('general.noResponse'),
          response?.data || undefined
        );
      }
      setIsLoading(false);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  }, [t, getFormParams, setIsLoading]);

  useEffect(() => {
    getData();
  }, [getData]);

  const resetData = () => {
    form.resetFields();
    setPage(1);
    setUnusualData([]);
    setSelectedRows([]);
    setSelectedGoodObjs(undefined);
    setGood(undefined);
    getData();
  };

  useEffect(() => {
    if (selectedGoodObjs) {
      form.setFieldsValue({ goodsId: selectedGoodObjs.goodsId });
      setGood(selectedGoodObjs);
      setSelectedGoodObjs(undefined);
    }
  }, [selectedGoodObjs, form]);

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter:
      | SorterResult<TaskGoodsUnusualDistrData>
      | SorterResult<TaskGoodsUnusualDistrData>[],
    extra: {
      currentDataSource: TaskGoodsUnusualDistrData[];
      action: 'paginate' | 'sort' | 'filter';
    }
  ) => {
    if (extra.action === 'sort' && !Array.isArray(sorter)) {
      if (sorter.field === 'unusualNum') {
        setSortValue('UNUSUAL_NUM');
      } else if (sorter.field === 'unusualRate') {
        setSortValue('UNUSUAL_RATE');
      }
    }
  };

  return (
    <>
      {fourZeroThree ? (
        <FourZeroThree />
      ) : fiveHundred ? (
        <FiveHundred />
      ) : (
        <Spin style={{ width: '100%' }} spinning={isLoading}>
          <Form /**Range picker, OK, Reset, Refresh Buttons */
            form={form}
            ref={formRef}
            initialValues={{}}
            style={{ marginBottom: 20 }}
          >
            <Row gutter={[16, 0]}>
              <Col span={24} md={24} lg={8} xl={8}>
                <Form.Item
                  name={'date'}
                  label={t('analysis.orderDistribution.date')}
                  initialValue={[moment(), moment()]}
                >
                  <RangePicker
                    style={{ width: '100%' }}
                    format={DATE_FORMAT}
                    placeholder={[
                      t('analysis.orderDistribution.startDate'),
                      t('analysis.orderDistribution.endDate'),
                    ]}
                    ranges={{
                      [`${t('nivo.week')}`]: [
                        moment().startOf('isoWeek'),
                        moment().endOf('isoWeek'),
                      ],
                      [`${t('nivo.month')}`]: [
                        moment().startOf('month'),
                        moment().endOf('month'),
                      ],
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={24} md={24} lg={8} xl={8}>
                <Form.Item
                  name="taskGoodsStatus"
                  label={t('analysis.taskDistribution.taskGoodsStatus')}
                  initialValue={
                    taskEnum?.unusualTaskGoodsStatus?.map(
                      (type) => type.code
                    ) ?? []
                  }
                >
                  <Select
                    placeholder={t('searchPlaceholders.selectStatus')}
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                    mode="multiple"
                    maxTagCount="responsive"
                  >
                    {taskEnum &&
                      taskEnum['unusualTaskGoodsStatus'].map((type) => (
                        <Select.Option key={type.code} value={type.code}>
                          {type.description}
                        </Select.Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={24} md={12} lg={12} xl={4}>
                <Form.Item
                  name="goodsId"
                  label={t('order.orderListColumns.advancedSearch.goods')}
                  style={{ marginBottom: 12 }}
                >
                  <Space>
                    {good ? (
                      <Space>
                        <Typography.Text
                          ellipsis={{ tooltip: good.fullGoodsName }}
                          style={{ maxWidth: 100 }}
                        >
                          {good.fullGoodsName}
                        </Typography.Text>
                        <CloseCircleOutlined
                          onClick={() => {
                            form.resetFields(['goodsId']);
                            setGood(undefined);
                          }}
                        />
                      </Space>
                    ) : (
                      <></>
                    )}
                    <Button
                      type="link"
                      onClick={() => setSelectGoodsVisible(true)}
                      style={{ padding: 0 }}
                    >
                      {t('actionsColumn.selectGood')}
                    </Button>
                  </Space>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Space align="start">
                  <Button
                    htmlType="submit"
                    type="primary"
                    onClick={() => {
                      if (typingTimeout) clearTimeout(typingTimeout);
                      setTypingTimeout(
                        setTimeout(() => {
                          getData();
                        }, GENERAL_TIMEOUT)
                      );
                    }}
                  >
                    {t('general.ok')}
                  </Button>
                  <Button
                    onClick={() => {
                      if (typingTimeout) clearTimeout(typingTimeout);
                      setTypingTimeout(
                        setTimeout(() => {
                          resetData();
                        }, GENERAL_TIMEOUT)
                      );
                    }}
                  >
                    {t('general.reset')}
                  </Button>
                </Space>
                <Tooltip title={t('general.refresh')}>
                  <Button
                    type="text"
                    onClick={() => {
                      if (typingTimeout) clearTimeout(typingTimeout);
                      setTypingTimeout(
                        setTimeout(() => {
                          getData();
                        }, GENERAL_TIMEOUT)
                      );
                    }}
                    icon={<ReloadOutlined />}
                  />
                </Tooltip>
              </Col>
            </Row>
          </Form>

          <TableToolbar
            setFontSize={setFontSize}
            fontSize={fontSize}
            tableSize={tableSize}
            setTableSize={setTableSize}
            refresh={() => getData()}
            totalItems={totalItem}
            columns={columns}
            columnKeys={columnKeys}
            selectedColumns={selectedColumns}
            setSelectedColumns={setSelectedColumns}
            rows={unusualData}
            exportConfig={{
              fileName: 'TASK_GOODS_UNUSUAL_STATS',
            }}
          />

          <Table<TaskGoodsUnusualDistrData>
            onChange={onTableChange}
            dataSource={unusualData}
            loading={isLoading}
            size={tableSize}
            columns={columns.filter((x) =>
              selectedColumns.includes(x.key?.toString() ?? '')
            )}
            components={vt}
            scroll={{ y: 600, x: 1200 }}
            rowKey={(row) => `${row.goodsId}`}
            rowSelection={{
              selectedRowKeys,
              onChange: (
                selectedRowKeys: React.Key[],
                selectedRows: TaskGoodsUnusualDistrData[]
              ) => {
                setSelectedRowKeys(selectedRowKeys);
                setSelectedRows(selectedRows);
              },
            }}
            pagination={{
              current: page,
              total: totalItem,
              showQuickJumper: true,
              showSizeChanger: true,
              showTotal: (total, range) =>
                t('general.paginationTotal', {
                  start: range[0],
                  end: range[1],
                  total: total,
                }),
              size: 'small',
              defaultPageSize: pageSize,
              onChange: (p, pSize) => {
                setPage(p);
                setPageSize(pSize || pageSize);
                setSelectedRowKeys([]);
              },
            }}
          />

          <SelectGoodsModal
            firstLoad={false}
            newGoodObjs={selectedGoodObjs}
            setNewGoodObjs={setSelectedGoodObjs}
            visible={selectGoodsVisible}
            setVisible={setSelectGoodsVisible}
            sellerId={
              // 这里没有SellerId参数，之后考虑弄
              form.getFieldValue('sellerId')
                ? Number(form.getFieldValue('sellerId'))
                : 0
            }
            multiSelectFeature={false}
          />

          {!!selectedRowKeys.length && (
            <TableFooterToolbar
              selectedRows={selectedRows}
              setSelectedRowKeys={setSelectedRowKeys}
              columns={columns}
              funct={{ exportConfig: { fileName: 'TASK_GOODS_UNUSUAL_STATS' } }}
            />
          )}
        </Spin>
      )}
    </>
  );
};

export default TaskGoodsUnusualDetail;
